import { queryOptions, useMutation } from '@tanstack/react-query';

import { getAccessToken } from '@/lib/tokenStorage';

import type { AnimalDto } from './index';

export type BreedOption = {
  id: string;
  name: string;
  code: string;
};

export type AccountOption = {
  id: string;
  name: string;
  code: string | null;
};

export type YardOption = {
  id: string;
  name: string;
};

export type PenOption = {
  id: string;
  name: string;
  yard_id: string | null;
};

export type CreateAnimalInput = {
  animalType: number;
  name?: string | null;
  tag?: string | null;
  tattoo?: string | null;
  metalTag?: string | null;
  registration?: string | null;
  pedigreeName?: string | null;
  breedId?: string | null;
  ownerId?: string | null;
  yardId?: string | null;
  penId?: string | null;
  colorId?: string | null;
  sex?: number | null;
  birth?: string | null;
  weight?: number | null;
  programCategory?: string | null;
  opuProgram?: string | null;
  pregnant?: number | null;
  ivfStatusId?: string | null;
  ocvStatusId?: string | null;
  recipientStatusId?: string | null;
  received?: string | null;
  shipOn?: string | null;
  /** Two-letter US state code. */
  origin?: string | null;
  /** Donor only. */
  hairSample?: boolean | null;
  /** Donor only, tog_heifercow picklist value. */
  heiferCow?: number | null;
  /** Recipient only. */
  lastDrugUsedId?: string | null;
  /** Recipient only, "Original Owner" (tog_Client) picklist value. */
  client?: number | null;
  /** Required to save over possible duplicates; kept in the audit history. */
  duplicateOverrideReason?: string | null;
};

export type AnimalPayload = {
  code: number;
  success: boolean;
  message: string | null;
  data: unknown;
  errors: Record<string, string> | null;
};

/** An existing active animal that a create may duplicate (409 from POST). */
export type PossibleDuplicate = {
  animal: AnimalDto;
  /** CreateAnimalInput field names that matched, e.g. metalTag, tag. */
  matchedOn: string[];
};

export const getPossibleDuplicates = (
  payload: AnimalPayload,
): PossibleDuplicate[] => {
  if (payload.code !== 409) return [];
  const data = payload.data as { possibleDuplicates?: unknown } | null;
  return Array.isArray(data?.possibleDuplicates)
    ? (data.possibleDuplicates as PossibleDuplicate[])
    : [];
};

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

function getHeaders(): HeadersInit {
  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

export async function createAnimal(
  input: CreateAnimalInput,
): Promise<AnimalPayload> {
  const response = await fetch(`${API_BASE_URL}/api/v1/animal`, {
    method: 'POST',
    credentials: 'include',
    headers: getHeaders(),
    body: JSON.stringify(input),
  });

  return (await response.json()) as AnimalPayload;
}

export function breedsQueryOptions({ search }: { search?: string } = {}) {
  return queryOptions({
    queryKey: ['lookups', 'breeds', { search }],
    async queryFn() {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      const query = params.toString();

      const response = await fetch(
        `${API_BASE_URL}/api/v1/lookups/breeds${query ? `?${query}` : ''}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: getHeaders(),
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return (await response.json()) as BreedOption[];
    },
  });
}

export function accountsQueryOptions({ search }: { search?: string } = {}) {
  return queryOptions({
    queryKey: ['lookups', 'accounts', { search }],
    async queryFn() {
      const params = new URLSearchParams();
      const trimmedSearch = search?.trim();
      if (trimmedSearch) params.set('search', trimmedSearch);
      const query = params.toString();

      const response = await fetch(
        `${API_BASE_URL}/api/v1/account/autocomplete${query ? `?${query}` : ''}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: getHeaders(),
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return (await response.json()) as AccountOption[];
    },
  });
}

export function yardsQueryOptions({ search }: { search?: string } = {}) {
  return queryOptions({
    queryKey: ['lookups', 'yards', { search }],
    async queryFn() {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      const query = params.toString();

      const response = await fetch(
        `${API_BASE_URL}/api/v1/lookups/yards${query ? `?${query}` : ''}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: getHeaders(),
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return (await response.json()) as YardOption[];
    },
  });
}

export function pensQueryOptions({ search }: { search?: string } = {}) {
  return queryOptions({
    queryKey: ['lookups', 'pens', { search }],
    async queryFn() {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      const query = params.toString();

      const response = await fetch(
        `${API_BASE_URL}/api/v1/lookups/pens${query ? `?${query}` : ''}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: getHeaders(),
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return (await response.json()) as PenOption[];
    },
  });
}

export const useCreateAnimal = () => {
  return useMutation({
    mutationFn: (input: CreateAnimalInput) => createAnimal(input),
  });
};

export const OPTION_SET_OBJECT_TYPES = {
  donor: 10012,
  calf: 10008,
  recipient: 10023,
} as const;

export type OptionSetAttribute = 'tog_opuprogram' | 'tog_category';

type StringMapItem = { key: number; value: string | null };

export function optionSetQueryOptions(
  objectTypeCode: number,
  attributeName: OptionSetAttribute,
) {
  return queryOptions({
    queryKey: ['lookups', 'string-map', objectTypeCode, attributeName],
    staleTime: 30 * 60 * 1000,
    async queryFn() {
      const params = new URLSearchParams({
        objectTypeCode: String(objectTypeCode),
        name: attributeName,
      });
      const response = await fetch(
        `${API_BASE_URL}/api/v1/lookups/string-map?${params.toString()}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: getHeaders(),
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const items = (await response.json()) as StringMapItem[];
      // The API writes by label, so labels are the option values.
      const seen = new Set<string>();
      return items
        .map((item) => item.value?.trim() ?? '')
        .filter((label) => {
          if (!label || seen.has(label)) return false;
          seen.add(label);
          return true;
        });
    },
  });
}

export type LookupOption = { id: string; name: string | null };

async function getLookup<T>(path: string, params: URLSearchParams) {
  const query = params.toString();
  const response = await fetch(
    `${API_BASE_URL}/api/v1/lookups/${path}${query ? `?${query}` : ''}`,
    { method: 'GET', credentials: 'include', headers: getHeaders() },
  );
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return (await response.json()) as T;
}

const searchParams = (search?: string) => {
  const params = new URLSearchParams();
  if (search) params.set('search', search);
  return params;
};

export function drugsQueryOptions({ search }: { search?: string } = {}) {
  return queryOptions({
    queryKey: ['lookups', 'drugs', { search }],
    queryFn: () => getLookup<LookupOption[]>('drugs', searchParams(search)),
  });
}

export function colorsQueryOptions({ search }: { search?: string } = {}) {
  return queryOptions({
    queryKey: ['lookups', 'colors', { search }],
    queryFn: () => getLookup<LookupOption[]>('colors', searchParams(search)),
  });
}

export type AnimalStatusKind = 'recipient' | 'ocv';

export function animalStatusOptionsQueryOptions(kind: AnimalStatusKind) {
  return queryOptions({
    queryKey: ['lookups', 'animal-status-options', kind],
    staleTime: 30 * 60 * 1000,
    queryFn: () =>
      getLookup<LookupOption[]>(
        'animal-status-options',
        new URLSearchParams({ kind }),
      ),
  });
}

export type KeyedStringMapAttribute =
  | 'tog_sex'
  | 'tog_client'
  | 'tog_heifercow';

/** Picklist options keyed by their stored value, for fields written by value. */
export function keyedStringMapQueryOptions(
  objectTypeCode: number,
  attributeName: KeyedStringMapAttribute,
) {
  return queryOptions({
    queryKey: ['lookups', 'string-map-keyed', objectTypeCode, attributeName],
    staleTime: 30 * 60 * 1000,
    async queryFn() {
      const items = await getLookup<StringMapItem[]>(
        'string-map',
        new URLSearchParams({
          objectTypeCode: String(objectTypeCode),
          name: attributeName,
        }),
      );
      const seen = new Set<number>();
      return items
        .filter((item) => {
          if (seen.has(item.key) || !item.value?.trim()) return false;
          seen.add(item.key);
          return true;
        })
        .map((item) => ({
          value: String(item.key),
          label: item.value!.trim(),
        }));
    },
  });
}
